import React, { useEffect, useRef } from 'react';
import { AccessibilityInfo, Animated, Easing, Platform, StyleSheet, Text, View } from 'react-native';
import { getGuideColors } from './GuideCardLayout';

const CARD_LABELS = {
  'gab-ui': 'interactive answer',
  recipe: 'recipe',
  workout: 'workout',
  mealplan: 'meal plan',
  checklist: 'checklist',
  itinerary: 'itinerary',
  comparison: 'comparison',
  studyplan: 'study plan',
  projectguide: 'project guide',
  eventplan: 'event plan',
  chart: 'chart',
  places: 'places',
};

const pulseStyles = `
  @keyframes gab-card-pulse { 0%, 100% { opacity: .5; } 50% { opacity: 1; } }
  [data-card-skeleton] { animation: gab-card-pulse 3.2s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) { [data-card-skeleton] { animation: none; opacity: .75; } }
`;

function StreamingCardPlaceholder({ kind, colors: themeColors, fontScale = 1 }) {
  const colors = getGuideColors(themeColors, kind);
  const label = kind === 'html' ? 'Generating your preview…' : `Preparing your ${CARD_LABELS[kind] || 'card'}…`;
  const opacity = useRef(new Animated.Value(0.5)).current;
  useEffect(() => {
    // CSS runs the web pulse without a JS frame loop. Native platforms use
    // the compositor, and both paths honor the system's motion preference.
    if (Platform.OS === 'web') return undefined;
    let active = true;
    let animation;
    const update = reduced => {
      if (!active) return;
      animation?.stop();
      opacity.setValue(reduced ? 0.75 : 0.5);
      if (reduced) return;
      const timing = toValue => Animated.timing(opacity, {
        toValue, duration: 1600, easing: Easing.inOut(Easing.ease), useNativeDriver: true, isInteraction: false,
      });
      animation = Animated.loop(Animated.sequence([timing(1), timing(0.5)]));
      animation.start();
    };
    AccessibilityInfo.isReduceMotionEnabled().then(update).catch(() => update(true));
    const listener = AccessibilityInfo.addEventListener('reduceMotionChanged', update);
    return () => { active = false; animation?.stop(); listener.remove(); };
  }, [opacity]);
  const Skeleton = Platform.OS === 'web' ? View : Animated.View;
  const barColor = themeColors.status_bar_style === 'dark' ? '#eeeeef' : '#333336';
  const bar = (width, height = 10) => <View style={[styles.line, { width, height, backgroundColor: barColor }]} />;

  return (
    <View
      accessibilityRole="progressbar"
      accessibilityLabel={label}
      accessibilityState={{ busy: true }}
      aria-busy
      dataSet={{ streamingCard: kind, copyExclude: '' }}
      style={[styles.container, { backgroundColor: colors.background, borderColor: colors.border }]}
    >
      {Platform.OS === 'web' && <style>{pulseStyles}</style>}
      <View accessible={false} importantForAccessibility="no-hide-descendants" aria-hidden>
        <Text style={[styles.label, { color: colors.secondary, fontSize: 13 * fontScale, lineHeight: 20 * fontScale }]}>{label}</Text>
      </View>
      <Skeleton dataSet={{ cardSkeleton: '' }} style={[styles.skeleton, Platform.OS !== 'web' && { opacity }]}
        accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" aria-hidden>
        {bar('56%', 20)}
        <View style={styles.description}>{bar('92%')}{bar('72%')}</View>
        <View style={[styles.details, { borderColor: colors.border }]}>
          {[0, 1, 2].map(index => <View key={index} style={styles.detail}>{bar('65%', 12)}{bar('42%', 8)}</View>)}
        </View>
      </Skeleton>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { width: '100%', maxWidth: '100%', minWidth: 0, borderWidth: 1, borderRadius: 24, padding: 24, marginVertical: 8 },
  label: { fontWeight: '500' },
  skeleton: { marginTop: 22 },
  line: { borderRadius: 6 },
  description: { marginTop: 18, gap: 10 },
  details: { flexDirection: 'row', marginTop: 24, paddingTop: 20, borderTopWidth: 1, gap: 16 },
  detail: { flex: 1, minWidth: 0, gap: 9 },
});

export default React.memo(StreamingCardPlaceholder);
